import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

// How long an optimistic row waits for the server before it is taken away. The server answers in ~1-2 s.
const OPTIMISTIC_ROW_TIMEOUT = 10000;

/**
 * The chosen tab, kept for the page view. It lives outside the element, so it also survives a render that replaces it.
 * @type {string | undefined}
 */
let activeTab;

/**
 * Tabs in the cart drawer upsell (see cart-upsell.liquid).
 *
 * The drawer is re-rendered after every cart change, and the server always selects the first tab. The chosen tab is
 * put back after each render, so adding a bundle doesn't jump back to the best sellers. If the chosen tab is gone
 * because everything in it is now in the cart, the first tab is shown.
 *
 * The render is morphed into the existing element without calling updatedCallback, so an observer watches for the
 * server's selection coming back instead.
 *
 * Adding a product shows it in the cart at once: a row is copied from an existing line and filled with the upsell's
 * title, image and price, and the upsell item is hidden. The server's response morphs the real cart over it, a second
 * later. If the add fails, the row is taken away and the upsell item shown again.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement[]} tabs
 * @property {HTMLElement[]} panels
 *
 * @extends {Component<Refs>}
 */
class CartUpsellComponent extends Component {
  requiredRefs = ['tabs', 'panels'];

  #observer = new MutationObserver(() => this.#render());

  /** @type {Set<{ row: HTMLElement, item: HTMLElement, timeout: ReturnType<typeof setTimeout> }>} */
  #optimistic = new Set();

  connectedCallback() {
    super.connectedCallback();
    this.#render();
    this.#observer.observe(this, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['aria-selected', 'hidden'],
    });

    // Listens as well as the product form, which sends the request: this only shows the result ahead of it
    this.addEventListener('submit', this.#addOptimistically);
    document.addEventListener(ThemeEvents.cartError, this.#undoOptimistic);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer.disconnect();
    this.removeEventListener('submit', this.#addOptimistically);
    document.removeEventListener(ThemeEvents.cartError, this.#undoOptimistic);
  }

  /**
   * @param {Event} event
   */
  #addOptimistically = (event) => {
    const item = event.target instanceof Element ? event.target.closest('.cart-upsell__item') : null;
    const rows = this.closest('cart-items-component')?.querySelector('tbody');
    const template = rows?.querySelector('tr.cart-items__table-row:not(.cart-items__nested-line)');

    // An empty cart has no row to copy. It waits for the server, like before.
    if (!(item instanceof HTMLElement) || !rows || !template) return;

    const row = this.#buildRow(template, item);

    // The cart lists the product added last first
    rows.prepend(row);
    item.style.display = 'none';

    const timeout = setTimeout(() => this.#undoOptimistic(), OPTIMISTIC_ROW_TIMEOUT);
    this.#optimistic.add({ row, item, timeout });
  };

  /**
   * A copy of an existing cart row, with what belongs to the other product replaced. Everything the copy is not given
   * is removed: variants, discounts, the sale badge. The real row replaces it.
   * @param {Element} template - A row from the cart
   * @param {HTMLElement} item - The upsell item that is added
   * @returns {HTMLElement}
   */
  #buildRow(template, item) {
    const row = /** @type {HTMLElement} */ (template.cloneNode(true));
    const link = item.querySelector('.cart-upsell__title');
    const image = item.querySelector('img');

    // Without ref the row is not one of the component's cart lines, which would shift the line numbers
    for (const element of [row, ...row.querySelectorAll('[ref]')]) element.removeAttribute('ref');
    row.removeAttribute('data-key');
    row.removeAttribute('data-parent-key');
    row.classList.add('cart-items__table-row--pending');
    row.setAttribute('inert', '');
    row.setAttribute('aria-busy', 'true');

    for (const anchor of row.querySelectorAll('.cart-items__media-container, .cart-items__title')) {
      anchor.setAttribute('href', link?.getAttribute('href') ?? '#');
    }

    const rowImage = row.querySelector('.cart-items__media-image');
    if (rowImage instanceof HTMLImageElement && image) {
      // The copy's srcset is for the other product
      rowImage.removeAttribute('srcset');
      rowImage.removeAttribute('sizes');
      rowImage.src = image.currentSrc || image.src;
      rowImage.alt = '';
    }
    row.querySelector('.cart-items__sale-badge')?.remove();

    const title = row.querySelector('.cart-items__title');
    if (title) title.textContent = link?.textContent?.trim() ?? '';

    const info = row.querySelector('.cart-items__product-info');
    const titleLine = title?.closest('p');
    if (info && titleLine) info.replaceChildren(titleLine);

    for (const input of row.querySelectorAll('input')) input.value = '1';

    const price = row.querySelector('.cart-items__price');
    if (price) {
      const text = document.createElement('text-component');
      text.textContent = item.dataset.price ?? '';
      price.replaceChildren(text);
    }

    return row;
  }

  /**
   * Takes the optimistic rows away and shows the upsell items again.
   */
  #undoOptimistic = () => {
    for (const entry of this.#optimistic) {
      clearTimeout(entry.timeout);

      // When the server's cart has been morphed over the row, it is a real line by now and has to stay
      if (entry.row.classList.contains('cart-items__table-row--pending')) entry.row.remove();
      if (entry.item.isConnected) entry.item.style.display = '';
    }

    this.#optimistic.clear();
  };

  /**
   * @param {MouseEvent} event
   */
  selectTab(event) {
    const tab = /** @type {HTMLElement} */ (event.target);

    activeTab = tab.dataset.tab;
    this.#render();
  }

  /**
   * Arrow keys, Home and End move between the tabs (the WAI-ARIA tabs pattern).
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    const { tabs } = this.refs;
    const index = tabs.indexOf(/** @type {HTMLButtonElement} */ (event.target));

    /** @type {HTMLButtonElement | undefined} */
    let next;

    switch (event.key) {
      case 'ArrowRight':
        next = tabs[(index + 1) % tabs.length];
        break;
      case 'ArrowLeft':
        next = tabs[(index - 1 + tabs.length) % tabs.length];
        break;
      case 'Home':
        next = tabs[0];
        break;
      case 'End':
        next = tabs[tabs.length - 1];
        break;
      default:
        return;
    }

    event.preventDefault();
    activeTab = next?.dataset.tab;
    this.#render();
    next?.focus();
  }

  /**
   * Queries the tabs instead of using refs: the observer can run before the refs have caught up with a render.
   * Only writes what differs, as every write would wake the observer again.
   */
  #render() {
    const tabs = /** @type {HTMLButtonElement[]} */ ([...this.querySelectorAll('[role="tab"]')]);
    const panels = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[role="tabpanel"]')]);
    const current = tabs.find((tab) => tab.dataset.tab === activeTab) ?? tabs[0];

    for (const tab of tabs) {
      const selected = tab === current;

      if (tab.getAttribute('aria-selected') !== `${selected}`) tab.setAttribute('aria-selected', `${selected}`);
      if (tab.tabIndex !== (selected ? 0 : -1)) tab.tabIndex = selected ? 0 : -1;
    }

    for (const panel of panels) {
      const hidden = panel.dataset.tab !== current?.dataset.tab;

      if (panel.hidden !== hidden) panel.hidden = hidden;
    }
  }
}

if (!customElements.get('cart-upsell-component')) {
  customElements.define('cart-upsell-component', CartUpsellComponent);
}
